// src/pages/Banners/index.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../contexts/LanguageContext";
import { fetchBanners, deleteBanner, updateBanner } from "../../redux/slices/bannerSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import NewBannerModal from "../../components/Banners/NewBannerModal";
import EditBannerModal from "../../components/Banners/EditBannerModal";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { 
  MagnifyingGlassIcon, 
  ChevronDownIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from "@heroicons/react/24/outline";

function Banners() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { banners = [], loading, error } = useSelector((state) => state.banners || {});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [bannerToEdit, setBannerToEdit] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openEditModal = (banner) => {
    setBannerToEdit(banner);
    setIsEditModalOpen(true);
  };

  // Filter and sort state
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortBy, setSortBy] = useState("Sequence");

  useEffect(() => {
    dispatch(fetchBanners({ limit: 100, currentPage: 1 }));
  }, [dispatch]);

  const openDeleteModal = (bannerId) => {
    setBannerToDelete(bannerId);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (!bannerToDelete) return;
    
    setIsDeleting(true);
    dispatch(deleteBanner(bannerToDelete))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ message: t("bannerForm.deletedSuccess"), type: "success" }));
        setIsDeleteModalOpen(false);
        setBannerToDelete(null);
      })
      .catch((error) => {
        dispatch(showNotification({
          message: error || t("bannerForm.failedDelete"),
          type: "error"
        }));
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleToggleStatus = (bannerId, currentStatus) => {
    // Find the banner to get its current data
    const banner = banners.find(b => b._id === bannerId);
    if (!banner) return;

    // Update the banner with the new status
    const updatedBannerData = {
      ...banner,
      isActive: !currentStatus
    };

    dispatch(updateBanner({ id: bannerId, bannerData: updatedBannerData }))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ 
          message: `Banner ${!currentStatus ? 'activated' : 'deactivated'} successfully!`, 
          type: "success" 
        }));
      })
      .catch(() => {
        dispatch(showNotification({ message: "Failed to update banner status", type: "error" }));
      });
  };

  // Handle image error by setting placeholder
  const handleImageError = (e) => {
    e.target.src = placeholderImage;
  };

  // Get image source with fallback to placeholder
  const getImageSrc = (banner) => {
    // Check multiple possible image properties and ensure they exist
    const imageUrl = banner?.image || banner?.coverImage || banner?.imageUrl;
    return imageUrl?.trim() ? imageUrl : placeholderImage;
  };

  // Process banners based on filter and sort
  const filteredBanners = banners.filter((banner) => {
    // Add null check for banner object
    if (!banner) return false;
    if (filterStatus === "Active" && !banner.isActive) return false;
    if (filterStatus === "Inactive" && banner.isActive) return false;
    return true;
  });

  const sortedBanners = filteredBanners.sort((a, b) => {
    // Add null checks for both banner objects
    if (!a || !b) return 0;
    
    if (sortBy === "Sequence") {
      return (a.sequenceNo || 0) - (b.sequenceNo || 0);
    }
    if (sortBy === "Created") {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    }
    if (sortBy === "Updated") {
      const dateA = a.updatedAt ? new Date(a.updatedAt) : new Date(0);
      const dateB = b.updatedAt ? new Date(b.updatedAt) : new Date(0);
      return dateB - dateA;
    }
    return 0;
  });

  return (
    <div>
      {/* Table Container */}
      <div className="mt-4 bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
        {/* Dark Header */}
        <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-white font-semibold text-lg">{t("banners.title")}</h2>
            <span className="text-white font-semibold">({sortedBanners.length})</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
              <input
                type="text"
                placeholder={t("common.search")}
                className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-28"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-transparent border border-white rounded px-3 py-1.5 text-white text-sm font-light outline-none cursor-pointer"
            >
              <option value="All" className="text-dashboardDark">{t("banners.filter.allStatus")}</option>
              <option value="Active" className="text-dashboardDark">{t("banners.filter.active")}</option>
              <option value="Inactive" className="text-dashboardDark">{t("banners.filter.inactive")}</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent border border-white rounded px-3 py-1.5 text-white text-sm font-light outline-none cursor-pointer"
            >
              <option value="Sequence" className="text-dashboardDark">{t("banners.sort.sequence")}</option>
              <option value="Created" className="text-dashboardDark">{t("banners.sort.created")}</option>
              <option value="Updated" className="text-dashboardDark">{t("banners.sort.updated")}</option>
            </select>
            
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-4 py-1.5 bg-dashboardCoral rounded text-white text-sm font-medium"
            >
              <PlusIcon className="w-[18px] h-[18px]" />
              <span>{t("common.addNew")}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
            <span className="ml-2">{t("banners.loading")}</span>
          </div>
        ) : error ? (
          <p className="p-4 text-red-500">Error: {error}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dashboardStroke">
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.image")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("banners.col.sequence")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.status")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.createdAt")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.updatedAt")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">{t("col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedBanners.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">
                      {t("banners.noFound")}
                    </td>
                  </tr>
                ) : (
                  sortedBanners
                    .filter(banner => banner && banner._id)
                    .map((banner, index) => (
                    <tr 
                      key={banner._id} 
                      className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                        index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                      }`}
                    >
                      <td className="px-4 py-4">
                        <img
                          src={getImageSrc(banner)}
                          alt="Banner"
                          className="h-12 w-20 object-cover rounded-md"
                          onError={handleImageError}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {banner?.sequenceNo || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                          banner?.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                            banner?.isActive ? 'bg-green-500' : 'bg-gray-400'
                          }`} />
                          {banner?.isActive ? t("common.active") : t("common.inactive")}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {banner?.createdAt ? new Date(banner.createdAt).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {banner?.updatedAt ? new Date(banner.updatedAt).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-0.5">
                          <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                            <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </button>
                          <div className="w-px h-6 bg-dashboardStroke"></div>
                          <button
                            onClick={() => openEditModal(banner)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <PencilIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </button>
                          <div className="w-px h-6 bg-dashboardStroke"></div>
                          <button
                            onClick={() => openDeleteModal(banner._id)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination info */}
        {banners.length > 0 && (
          <div className="px-4 py-3 border-t border-dashboardStroke text-sm text-gray-700">
            {t("banners.showing")} {sortedBanners.length} {t("banners.showingBanners")}
          </div>
        )}
      </div>

      {/* New Banner Modal */}
      {isAddModalOpen && (
        <NewBannerModal
          isOpen={isAddModalOpen}
          setIsOpen={setIsAddModalOpen}
        />
      )}

      {/* Edit Banner Modal */}
      {isEditModalOpen && bannerToEdit && (
        <EditBannerModal
          isOpen={isEditModalOpen}
          setIsOpen={(open) => {
            setIsEditModalOpen(open);
            if (!open) setBannerToEdit(null);
          }}
          banner={bannerToEdit}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t("deleteConfirm.banner.title")}
        message={t("deleteConfirm.banner.message")}
        confirmText={t("deleteConfirm.delete")}
        cancelText={t("deleteConfirm.cancel")}
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default Banners;